import { MutationResult } from "@/api/utils/types"
import { useMutation } from "@/api/utils/useQuery"
import Cache from "@/api/utils/Cache"
import {
  BackofficeRestMutations,
  CreateRenewalPayload,
} from "@/api/backofficeRest/mutations"
import { CreateRenewalResponse } from "@/api/backofficeRest/generated/model"
import { useCurrentAccountStore } from "@/stores/currentAccount"
import { formatDateDashed } from "@/helpers/dateHelper"
import { IRenewAgreement } from "./AgreementMutator.types"

export const PAYMENT_SCHEDULE_NOT_APPLIED = "paymentScheduleNotApplied"

export const hasPaymentScheduleWarning = (response: unknown): boolean => {
  // warnings ships in the backoffice contract BE-first, so it is not yet in the generated REST model
  const warnings =
    (response as { warnings?: readonly (string | null)[] } | null)?.warnings ??
    []
  return warnings.includes(PAYMENT_SCHEDULE_NOT_APPLIED)
}

export interface IAgreementMutator {
  renew(): MutationResult<IRenewAgreement, CreateRenewalResponse>
}

class AgreementMutatorClass implements IAgreementMutator {
  renew(): MutationResult<IRenewAgreement, CreateRenewalResponse> {
    return useMutation({
      ...BackofficeRestMutations.renewal.create(),
      serializerFn: (data: IRenewAgreement): CreateRenewalPayload => {
        const billingAccountId = useCurrentAccountStore().coreId.data
        if (billingAccountId == null) throw new Error("Missing coreId")

        return {
          billingAccountId,
          input: {
            creditExitFee: data.creditExitFee,
            paymentSchedule: {
              amount: data.paymentSchedule.amount,
              seasonal: data.paymentSchedule.seasonal,
              estimatedAnnualCost: data.paymentSchedule.estimatedAnnualCost,
              firstPaymentDate: formatDateDashed(
                data.paymentSchedule.firstPaymentDate,
              ),
              paymentScheduleFromDate: data.paymentSchedule
                .paymentScheduleFromDate
                ? formatDateDashed(data.paymentSchedule.paymentScheduleFromDate)
                : undefined,
            },
            newAgreements: data.newAgreements.map((agreement) => ({
              meterPointIdentifier: agreement.meterPointIdentifier,
              productCode: agreement.productCode,
              startDate: formatDateDashed(agreement.startDate),
            })),
          },
        }
      },
      onSuccessFn: () => {
        Cache.clear()
      },
    })
  }
}

export const AgreementMutator = new AgreementMutatorClass()
